// src/components/StrategyList/StrategyList.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Chip,
  Grid,
  IconButton,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  FlashOn as SignalIcon,
  Visibility as ViewIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import strategyAPI from '../../services/strategyService';

const StrategyList = () => {
  const navigate = useNavigate();
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, strategy: null });
  const [resetDialog, setResetDialog] = useState({ open: false, strategy: null });

  useEffect(() => {
    loadStrategies();
  }, []);

  const loadStrategies = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Fetching strategies...');
      const data = await strategyAPI.listStrategies();
      console.log('Strategies data:', data);
      
      if (data && data.strategies) {
        setStrategies(data.strategies);
      } else {
        setStrategies([]);
      }
    } catch (err) {
      console.error('Error loading strategies:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStrategy = async () => {
    if (!deleteDialog.strategy) return;
    
    try {
      await strategyAPI.deleteStrategy(deleteDialog.strategy.strategy_id);
      setDeleteDialog({ open: false, strategy: null });
      loadStrategies();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleResetStrategy = async () => {
    if (!resetDialog.strategy) return;
    
    try {
      await strategyAPI.resetStrategy(resetDialog.strategy.strategy_id);
      setResetDialog({ open: false, strategy: null });
      loadStrategies();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleViewDetails = (strategy) => {
    navigate(`/admin/strategy-builder/${strategy.strategy_id}`);
  };

  const handleEditStrategy = (strategy) => {
    navigate(`/admin/strategy-builder/edit/${strategy.strategy_id}`);
  };

  const handleCreateStrategy = () => {
    navigate('/admin/strategy-builder/create');
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={48} />
          <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
            Loading strategies...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                My Strategies
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Manage your trading strategies and monitor their performance
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={loadStrategies}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.3)',
                  },
                }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateStrategy}
                sx={{
                  bgcolor: 'white',
                  color: '#667eea',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: '#f8fafc',
                  },
                }}
              >
                Create Strategy
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Debug Info */}
        {process.env.NODE_ENV === 'development' && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            Found {strategies.length} strategies
          </Alert>
        )}

        {/* Strategies Grid */}
        {strategies.length === 0 ? (
          <Paper
            sx={{
              p: 8,
              textAlign: 'center',
              borderRadius: 3,
              border: '2px dashed #cbd5e1',
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                mb: 3,
              }}
            >
              <SignalIcon sx={{ fontSize: 40, color: '#3b82f6' }} />
            </Box>
            
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                color: '#1e293b',
                mb: 1,
              }}
            >
              No Strategies Yet
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}
            >
              Create your first trading strategy to start tracking signals and conditions
            </Typography>
            
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={handleCreateStrategy}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.25)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  boxShadow: '0 6px 16px rgba(102, 126, 234, 0.35)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.2s',
              }}
            >
              Create Your First Strategy
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {strategies.map((strategy) => (
              <Grid item xs={12} md={6} lg={4} key={strategy.strategy_id}>
                <Card
                  sx={{
                    borderRadius: 3,
                    border: '2px solid #e2e8f0',
                    height: '100%',
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: '#667eea',
                      boxShadow: '0 8px 24px rgba(102, 126, 234, 0.15)',
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* Strategy Header */}
                    <Stack direction="row" justifyContent="space-between" alignItems="start" sx={{ mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: '#1e293b',
                            mb: 0.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {strategy.strategy_id}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Chip
                            label={strategy.symbol}
                            size="small"
                            sx={{
                              bgcolor: '#dbeafe',
                              color: '#1e40af',
                              fontWeight: 700,
                              fontSize: '0.75rem',
                            }}
                          />
                          <Chip
                            label={strategy.timeframe}
                            size="small"
                            sx={{
                              bgcolor: '#f3e8ff',
                              color: '#6b21a8',
                              fontWeight: 700,
                              fontSize: '0.75rem',
                            }}
                          />
                        </Stack>
                      </Box>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    {/* Strategy Stats */}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12}>
                        <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#f5f3ff', borderRadius: 2, textAlign: 'center' }}>
                          <Typography variant="h5" sx={{ fontWeight: 800, color: '#8b5cf6' }}>
                            {strategy.conditions?.length || 0}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6b21a8', fontWeight: 600 }}>
                            Conditions
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    {/* Status Badge */}
                    <Chip
                      icon={<CheckIcon sx={{ fontSize: '16px !important' }} />}
                      label="Active"
                      size="small"
                      sx={{
                        bgcolor: '#d1fae5',
                        color: '#047857',
                        fontWeight: 700,
                        border: '2px solid #a7f3d0',
                        mb: 2,
                        '& .MuiChip-icon': {
                          color: '#047857',
                        },
                      }}
                    />

                    <Divider sx={{ my: 2 }} />

                    {/* Action Buttons */}
                    <Stack direction="row" spacing={1}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<ViewIcon />}
                        onClick={() => handleViewDetails(strategy)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          borderColor: '#e2e8f0',
                          color: '#64748b',
                          '&:hover': {
                            borderColor: '#667eea',
                            bgcolor: alpha('#667eea', 0.05),
                            color: '#667eea',
                          },
                        }}
                      >
                        View
                      </Button>
                      <IconButton
                        onClick={() => handleEditStrategy(strategy)}
                        sx={{
                          border: '1px solid #e2e8f0',
                          color: '#3b82f6',
                          '&:hover': {
                            bgcolor: '#eff6ff',
                            borderColor: '#bfdbfe',
                          },
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => setResetDialog({ open: true, strategy })}
                        sx={{
                          border: '1px solid #e2e8f0',
                          color: '#f59e0b',
                          '&:hover': {
                            bgcolor: '#fef3c7',
                            borderColor: '#fde68a',
                          },
                        }}
                      >
                        <ResetIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => setDeleteDialog({ open: true, strategy })}
                        sx={{
                          border: '1px solid #e2e8f0',
                          color: '#ef4444',
                          '&:hover': {
                            bgcolor: '#fef2f2',
                            borderColor: '#fecaca',
                          },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, strategy: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#1e293b' }}>
          Delete Strategy
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary">
            Are you sure you want to delete <strong>{deleteDialog.strategy?.strategy_id}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={() => setDeleteDialog({ open: false, strategy: null })}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteStrategy}
            variant="contained"
            color="error"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Confirmation Dialog */}
      <Dialog
        open={resetDialog.open}
        onClose={() => setResetDialog({ open: false, strategy: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#1e293b' }}>
          Reset Strategy
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary">
            Are you sure you want to reset <strong>{resetDialog.strategy?.strategy_id}</strong>? This will clear all detected signals and reset the strategy state.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={() => setResetDialog({ open: false, strategy: null })}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResetStrategy}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#f59e0b',
              '&:hover': {
                bgcolor: '#d97706',
              },
            }}
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StrategyList;
